<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">소속 신청 관리</h1>
          <p class="text-sm text-slate-500">파트너들의 현장 소속 신청을 검토하고 승인/반려 처리하세요</p>
        </div>
      </div>

      <!-- 필터 탭 -->
      <div class="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="currentTab = tab.value"
          :class="[
            'flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all whitespace-nowrap',
            currentTab === tab.value
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          ]"
        >
          {{ tab.label }}
          <span
            v-if="getCountByStatus(tab.value) > 0"
            :class="[
              'px-2 py-0.5 rounded-full text-xs font-semibold',
              currentTab === tab.value ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
            ]"
          >
            {{ getCountByStatus(tab.value) }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p class="text-slate-500">신청 목록을 불러오는 중...</p>
      </div>

      <!-- 신청 목록 -->
      <div v-else class="space-y-3">
        <div
          v-for="app in filteredApplications"
          :key="app.id"
          class="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex items-center gap-4 hover:shadow-md transition-all"
        >
          <!-- 상태 표시 -->
          <div class="flex-shrink-0">
            <span :class="[
              'px-3 py-1 rounded-full text-xs font-semibold',
              app.status === 'pending' ? 'bg-amber-100 text-amber-700' :
              app.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
              'bg-red-100 text-red-700'
            ]">
              {{ getStatusLabel(app.status) }}
            </span>
          </div>

          <!-- 신청자 정보 -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1 flex-wrap">
              <h3 class="font-semibold text-slate-800">{{ app.name }}</h3>
              <span class="text-sm text-slate-500">{{ app.position }} · {{ app.company }}</span>
            </div>
            <p class="flex items-center gap-1 text-sm text-blue-600 font-medium mb-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
              </svg>
              {{ app.propertyName }}
            </p>
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span class="flex items-center gap-1 text-xs text-slate-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                {{ app.phone }}
              </span>
              <span class="flex items-center gap-1 text-xs text-slate-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                {{ app.partnerEmail }}
              </span>
              <span class="flex items-center gap-1 text-xs text-slate-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                {{ formatDate(app.createdAt) }}
              </span>
            </div>
          </div>

          <!-- 액션 버튼 -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <button @click="openDetailModal(app)" class="px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition">
              상세보기
            </button>
            <template v-if="app.status === 'pending'">
              <button @click="handleApprove(app)" class="px-4 py-2 text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg transition">
                승인
              </button>
              <button @click="openRejectModal(app)" class="px-4 py-2 text-sm font-medium bg-red-500 text-white hover:bg-red-600 rounded-lg transition">
                반려
              </button>
            </template>
          </div>
        </div>

        <!-- 빈 상태 -->
        <div v-if="filteredApplications.length === 0" class="bg-white rounded-2xl p-12 shadow-sm border border-slate-100 text-center">
          <div class="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <p class="text-lg font-semibold text-slate-500">{{ getEmptyMessage() }}</p>
        </div>
      </div>

      <!-- 상세보기 모달 -->
      <Teleport to="body">
        <div v-if="showDetailModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="closeDetailModal">
          <div class="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div class="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 class="text-lg font-semibold text-slate-800">소속 신청 상세</h3>
              <button @click="closeDetailModal" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-5 overflow-y-auto flex-1">
              <!-- 상태 -->
              <div class="flex items-center justify-between mb-6">
                <span :class="[
                  'px-4 py-1.5 rounded-full text-sm font-semibold',
                  selectedApp?.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                  selectedApp?.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                  'bg-red-100 text-red-700'
                ]">
                  {{ getStatusLabel(selectedApp?.status) }}
                </span>
                <span class="text-sm text-slate-500">신청일: {{ formatDate(selectedApp?.createdAt) }}</span>
              </div>

              <!-- 신청 현장 -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-800 mb-3">신청 현장</h4>
                <div class="flex items-center gap-2 p-3 bg-blue-50 rounded-xl text-blue-600 font-medium">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
                  </svg>
                  <span>{{ selectedApp?.propertyName }}</span>
                </div>
              </div>

              <!-- 기본 정보 -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-800 mb-3">기본 정보</h4>
                <div class="grid grid-cols-2 gap-3">
                  <div class="bg-slate-50 rounded-xl p-3">
                    <span class="block text-xs text-slate-500 mb-1">이름</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.name }}</span>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-3">
                    <span class="block text-xs text-slate-500 mb-1">연락처</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.phone }}</span>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-3">
                    <span class="block text-xs text-slate-500 mb-1">소속 회사</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.company }}</span>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-3">
                    <span class="block text-xs text-slate-500 mb-1">직함/직급</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.position }}</span>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-3 col-span-2">
                    <span class="block text-xs text-slate-500 mb-1">이메일</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.partnerEmail }}</span>
                  </div>
                  <div v-if="selectedApp?.experience" class="bg-slate-50 rounded-xl p-3 col-span-2">
                    <span class="block text-xs text-slate-500 mb-1">경력 사항</span>
                    <span class="block text-sm text-slate-800 font-medium">{{ selectedApp?.experience }}</span>
                  </div>
                </div>
              </div>

              <!-- 영업 정보 -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-800 mb-3">영업 정보</h4>
                <div class="grid grid-cols-2 gap-3">
                  <div class="bg-slate-50 rounded-xl p-3">
                    <span class="block text-xs text-slate-500 mb-1">영업명</span>
                    <span class="block text-sm text-blue-600 font-medium">{{ selectedApp?.displayName }}</span>
                  </div>
                  <div class="bg-slate-50 rounded-xl p-3 col-span-2">
                    <span class="block text-xs text-slate-500 mb-1">한 줄 소개</span>
                    <span class="block text-sm text-slate-800 font-medium">"{{ selectedApp?.introduction }}"</span>
                  </div>
                </div>
              </div>

              <!-- 명함 이미지 -->
              <div class="mb-6">
                <h4 class="text-sm font-semibold text-slate-800 mb-3">명함 이미지</h4>
                <div class="text-center">
                  <img
                    v-if="selectedApp?.businessCardUrl"
                    :src="selectedApp.businessCardUrl"
                    alt="명함"
                    class="max-h-64 mx-auto rounded-xl border border-slate-200 cursor-pointer hover:shadow-lg transition"
                    @click="openImageInNewTab(selectedApp.businessCardUrl)"
                  />
                  <p class="text-xs text-slate-400 mt-2">클릭하면 새 탭에서 원본 이미지를 볼 수 있습니다</p>
                </div>
              </div>

              <!-- 반려 사유 (반려된 경우) -->
              <div v-if="selectedApp?.status === 'rejected' && selectedApp?.rejectReason" class="mb-6">
                <h4 class="text-sm font-semibold text-red-600 mb-3">반려 사유</h4>
                <div class="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
                  {{ selectedApp.rejectReason }}
                </div>
              </div>
            </div>

            <div v-if="selectedApp?.status === 'pending'" class="flex items-center justify-end gap-3 p-5 border-t border-slate-100">
              <button @click="closeDetailModal" class="px-5 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition">닫기</button>
              <button @click="openRejectModal(selectedApp); closeDetailModal()" class="px-4 py-2 text-sm font-medium bg-red-500 text-white hover:bg-red-600 rounded-lg transition">
                반려
              </button>
              <button @click="handleApprove(selectedApp); closeDetailModal()" class="px-4 py-2 text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg transition">
                승인하기
              </button>
            </div>
            <div v-else class="flex items-center justify-end gap-3 p-5 border-t border-slate-100">
              <button @click="closeDetailModal" class="px-5 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition">닫기</button>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- 반려 사유 모달 -->
      <Teleport to="body">
        <div v-if="showRejectModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="closeRejectModal">
          <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div class="flex items-center justify-between p-5 border-b border-slate-100">
              <h3 class="text-lg font-semibold text-slate-800">반려 처리</h3>
              <button @click="closeRejectModal" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-5">
              <div class="p-4 bg-slate-50 rounded-xl mb-4">
                <p class="font-semibold text-slate-800">{{ rejectTarget?.name }} ({{ rejectTarget?.company }})</p>
                <p class="text-sm text-slate-500">{{ rejectTarget?.propertyName }}</p>
              </div>

              <div class="mb-4">
                <label class="block text-sm font-medium text-slate-700 mb-2">반려 사유 <span class="text-red-500">*</span></label>
                <textarea
                  v-model="rejectReason"
                  placeholder="반려 사유를 입력해주세요. (예: 명함 이미지가 불명확합니다. 재신청 부탁드립니다.)"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 outline-none transition text-sm resize-none"
                  rows="4"
                ></textarea>
              </div>
            </div>

            <div class="flex items-center justify-end gap-3 p-5 border-t border-slate-100">
              <button @click="closeRejectModal" class="px-5 py-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition">취소</button>
              <button
                @click="handleReject"
                :disabled="!rejectReason.trim() || processing"
                class="px-5 py-2.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                {{ processing ? '처리 중...' : '반려 처리' }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'

export default {
  name: 'PartnerApplicationsView',
  components: {
    AdminLayout
  },
  setup() {
    const applications = ref([])
    const loading = ref(true)
    const currentTab = ref('pending')
    const processing = ref(false)

    const showDetailModal = ref(false)
    const selectedApp = ref(null)

    const showRejectModal = ref(false)
    const rejectTarget = ref(null)
    const rejectReason = ref('')

    const tabs = [
      { label: '대기중', value: 'pending' },
      { label: '승인됨', value: 'approved' },
      { label: '반려됨', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const filteredApplications = computed(() => {
      if (currentTab.value === 'all') {
        return applications.value
      }
      return applications.value.filter(app => app.status === currentTab.value)
    })

    const getCountByStatus = (status) => {
      if (status === 'all') {
        return applications.value.length
      }
      return applications.value.filter(app => app.status === status).length
    }

    const getStatusLabel = (status) => {
      const labels = {
        'pending': '대기중',
        'approved': '승인됨',
        'rejected': '반려됨'
      }
      return labels[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        'pending': 'badge-pending',
        'approved': 'badge-approved',
        'rejected': 'badge-rejected'
      }
      return classes[status] || ''
    }

    const getEmptyMessage = () => {
      const messages = {
        'pending': '대기중인 신청이 없습니다',
        'approved': '승인된 신청이 없습니다',
        'rejected': '반려된 신청이 없습니다',
        'all': '신청 내역이 없습니다'
      }
      return messages[currentTab.value]
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    const openDetailModal = (app) => {
      selectedApp.value = app
      showDetailModal.value = true
    }

    const closeDetailModal = () => {
      showDetailModal.value = false
      selectedApp.value = null
    }

    const openRejectModal = (app) => {
      rejectTarget.value = app
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const closeRejectModal = () => {
      showRejectModal.value = false
      rejectTarget.value = null
      rejectReason.value = ''
    }

    const openImageInNewTab = (url) => {
      window.open(url, '_blank')
    }

    const handleApprove = async (app) => {
      if (!confirm(`${app.name}님의 소속 신청을 승인하시겠습니까?`)) return

      processing.value = true
      try {
        // 신청 상태 업데이트
        await updateDoc(doc(db, 'partnerApplications', app.id), {
          status: 'approved',
          updatedAt: new Date().toISOString(),
          approvedAt: new Date().toISOString()
        })

        // propertyPartners 컬렉션에 파트너 추가
        await addDoc(collection(db, 'propertyPartners'), {
          propertyId: app.propertyId,
          propertyName: app.propertyName,
          partnerId: app.partnerId,
          partnerEmail: app.partnerEmail,
          name: app.name,
          phone: app.phone,
          company: app.company,
          position: app.position,
          displayName: app.displayName,
          introduction: app.introduction,
          businessCardUrl: app.businessCardUrl,
          applicationId: app.id,
          status: 'active',
          createdAt: new Date().toISOString()
        })

        // 사용자 role을 partner로 업그레이드 (아직 partner가 아닌 경우)
        const userDoc = await getDoc(doc(db, 'users', app.partnerId))
        if (userDoc.exists() && userDoc.data().role === 'user') {
          await updateDoc(doc(db, 'users', app.partnerId), {
            role: 'partner',
            updatedAt: new Date().toISOString()
          })
        }

        alert('승인 처리되었습니다.')
      } catch (error) {
        console.error('승인 처리 실패:', error)
        alert('승인 처리 중 오류가 발생했습니다.')
      } finally {
        processing.value = false
      }
    }

    const handleReject = async () => {
      if (!rejectReason.value.trim()) return

      processing.value = true
      try {
        await updateDoc(doc(db, 'partnerApplications', rejectTarget.value.id), {
          status: 'rejected',
          rejectReason: rejectReason.value.trim(),
          updatedAt: new Date().toISOString(),
          rejectedAt: new Date().toISOString()
        })

        alert('반려 처리되었습니다.')
        closeRejectModal()
      } catch (error) {
        console.error('반려 처리 실패:', error)
        alert('반려 처리 중 오류가 발생했습니다.')
      } finally {
        processing.value = false
      }
    }

    onMounted(() => {
      const q = query(
        collection(db, 'partnerApplications'),
        orderBy('createdAt', 'desc')
      )

      onSnapshot(q, (snapshot) => {
        applications.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        loading.value = false
      })
    })

    return {
      applications,
      loading,
      currentTab,
      processing,
      tabs,
      filteredApplications,
      showDetailModal,
      selectedApp,
      showRejectModal,
      rejectTarget,
      rejectReason,
      getCountByStatus,
      getStatusLabel,
      getStatusBadgeClass,
      getEmptyMessage,
      formatDate,
      openDetailModal,
      closeDetailModal,
      openRejectModal,
      closeRejectModal,
      openImageInNewTab,
      handleApprove,
      handleReject
    }
  }
}
</script>

